import { ASK_ASPER_BASE_URL } from '../config';
import type {
    EvalGroundTruthRequest,
    EvalGroundTruthResponse,
    EvalGroundTruthUpdateRequest,
    EvalGroundTruthHistoryEntry,
    EvalFromChatDraft,
    EvalListParams,
    EvalExportParams,
    CommentCreateRequest,
    QuestionSetInfo,
    EvalRunResponse,
    EvalRunResultsResponse,
    TriggerEvalRunPayload,
    EvalRunListParams,
} from '../eval/types';

const BASE = `${ASK_ASPER_BASE_URL}eval/ground-truth`;

function headers(json = true): HeadersInit {
    const h: Record<string, string> = {
        Accept: 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    };
    const email = localStorage.getItem('email');
    if (email) h['X-User-Email'] = email;
    if (json) h['Content-Type'] = 'application/json';
    return h;
}

async function handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}: ${body}`);
    }
    return res.json();
}

export async function getGroundTruthSets(): Promise<QuestionSetInfo[]> {
    const res = await fetch(`${BASE}/sets`, { headers: headers() });
    return handleResponse(res);
}

export async function getGroundTruthFromChat(
    threadId: string,
    runId: string,
    questionSet?: string,
): Promise<EvalFromChatDraft> {
    const params = questionSet ? `?question_set=${encodeURIComponent(questionSet)}` : '';
    const res = await fetch(`${BASE}/from-chat/${threadId}/${runId}${params}`, { headers: headers() });
    return handleResponse(res);
}

export async function listGroundTruth(params: EvalListParams = {}): Promise<EvalGroundTruthResponse[]> {
    const qs = new URLSearchParams();
    if (params.question_set) qs.set('question_set', params.question_set);
    if (params.status) qs.set('status', params.status);
    if (params.offset != null) qs.set('offset', String(params.offset));
    if (params.limit != null) qs.set('limit', String(params.limit));
    const q = qs.toString();
    const res = await fetch(`${BASE}${q ? `?${q}` : ''}`, { headers: headers() });
    return handleResponse(res);
}

export async function getGroundTruth(id: string): Promise<EvalGroundTruthResponse> {
    const res = await fetch(`${BASE}/${id}`, { headers: headers() });
    return handleResponse(res);
}

export async function createGroundTruth(body: EvalGroundTruthRequest): Promise<EvalGroundTruthResponse> {
    const payload = { ...body, created_by: localStorage.getItem('email') || undefined };
    const res = await fetch(BASE, { method: 'POST', headers: headers(), body: JSON.stringify(payload) });
    return handleResponse(res);
}

export async function updateGroundTruth(
    id: string,
    body: EvalGroundTruthUpdateRequest,
): Promise<EvalGroundTruthResponse> {
    const res = await fetch(`${BASE}/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) });
    return handleResponse(res);
}

export async function addGroundTruthComment(
    entryId: string,
    text: string,
): Promise<EvalGroundTruthResponse> {
    const body: CommentCreateRequest = { text };
    const res = await fetch(`${BASE}/${entryId}/comments`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(body),
    });
    return handleResponse(res);
}

export async function deleteGroundTruth(id: string): Promise<void> {
    const res = await fetch(`${BASE}/${id}`, { method: 'DELETE', headers: headers() });
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}: ${body}`);
    }
}

export async function getGroundTruthHistory(id: string): Promise<EvalGroundTruthHistoryEntry[]> {
    const res = await fetch(`${BASE}/${id}/history`, { headers: headers() });
    return handleResponse(res);
}

export async function exportGroundTruth(params: EvalExportParams = {}): Promise<Blob> {
    const qs = new URLSearchParams();
    if (params.question_set) qs.set('question_set', params.question_set);
    if (params.status) qs.set('status', params.status);
    const q = qs.toString();
    const res = await fetch(`${BASE}/export${q ? `?${q}` : ''}`, { headers: headers() });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.blob();
}

export async function importGroundTruth(body: unknown): Promise<unknown> {
    const res = await fetch(`${BASE}/import`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
    return handleResponse(res);
}

export async function refreshGroundTruthSql(
    entryId: string,
    regenerateViz = false,
): Promise<EvalGroundTruthResponse> {
    const qs = regenerateViz ? '?regenerate_viz=true' : '';
    const res = await fetch(`${BASE}/${entryId}/refresh-sql${qs}`, { method: 'POST', headers: headers() });
    return handleResponse(res);
}

// --- Eval Runs ---

const EVAL_BASE = `${ASK_ASPER_BASE_URL}eval`;

export async function getEvalConfigs(): Promise<string[]> {
    const res = await fetch(`${EVAL_BASE}/configs`, { headers: headers() });
    const data = await handleResponse<{ configs: string[] }>(res);
    return data.configs;
}

export async function triggerEvalRun(payload: TriggerEvalRunPayload): Promise<EvalRunResponse> {
    const res = await fetch(`${EVAL_BASE}/runs`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
    });
    return handleResponse(res);
}

export async function listEvalRuns(params: EvalRunListParams = {}): Promise<EvalRunResponse[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.question_set) qs.set('question_set', params.question_set);
    if (params.target_agent) qs.set('target_agent', params.target_agent);
    if (params.offset != null) qs.set('offset', String(params.offset));
    if (params.limit != null) qs.set('limit', String(params.limit));
    const q = qs.toString();
    const res = await fetch(`${EVAL_BASE}/runs${q ? `?${q}` : ''}`, { headers: headers() });
    return handleResponse(res);
}

export async function getEvalRun(runId: number): Promise<EvalRunResponse> {
    const res = await fetch(`${EVAL_BASE}/runs/${runId}`, { headers: headers() });
    return handleResponse(res);
}

export async function getEvalRunResults(runId: number): Promise<EvalRunResultsResponse> {
    const res = await fetch(`${EVAL_BASE}/runs/${runId}/results`, { headers: headers() });
    return handleResponse(res);
}

export async function downloadEvalResults(runId: number, format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    const res = await fetch(`${EVAL_BASE}/runs/${runId}/results/download?format=${format}`, {
        headers: headers(),
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.blob();
}
